// import moment from "moment";
import moment from 'moment-timezone'

/**
 * Validates if the provided date string is a valid date.
 *
 * @param {string} yourDate - The date string to validate.
 * @returns {boolean} Returns `true` if the date string is valid, otherwise `false`.
 */
const isValidDate = (yourDate) =>
    !yourDate || new Date(`${yourDate}`).toString() === 'Invalid Date'
        ? false
        : true

/**
 * Formats a given date or the current date into a specified format.
 *
 * @param {Date|string} yourDate - The date to be formatted. Can be a Date object or a string.
 * @param {string} [format="MM/DD/YYYY"] - The desired format for the date.
 * @returns {string} The formatted date as a string.
 */
const formatDate = (yourDate, format = 'MM/DD/YYYY') => {
    if (!isValidDate(yourDate)) {
        return moment().format(format)
    }

    return moment(yourDate).format(format)
}

/**
 * Formats a given date into a specified string format, adjusted for a specific timezone.
 *
 * @param {Date|string} yourDate - The date to be formatted. It can be a Date object or a date string.
 * @param {string} timezone - The timezone to adjust the date to, e.g., "America/New_York".
 * @param {string} [format="MM/DD/YYYY HH:mm:ss"] - The desired format for the output date string.
 * @returns {string} The formatted date string in the specified timezone and format. If the input date is invalid,
 * it returns the current date and time in the default format.
 */
const formatTzDateTime = (
    yourDate,
    timezone,
    format = 'MM/DD/YYYY HH:mm:ss'
) => {
    if (!isValidDate(yourDate)) {
        return moment().format(format)
    }

    const cutoffString = yourDate // in utc
    const utcCutoff = moment.utc(cutoffString)
    const displayCutoff = utcCutoff.clone().tz(timezone).format(format)

    return displayCutoff
}

/**
 * Gets the current date in the specified timezone formatted as 'YYYY-MM-DD'.
 *
 * @param {string|Date} datetime - The date and time from which to extract the day.
 * @param {string} timezone - The timezone to apply when extracting the day.
 * @returns {string} The formatted date string representing the current day in the specified timezone.
 */
const getTzTodayDay = (datetime, timezone) => {
    return moment(datetime).tz(timezone).format('YYYY-MM-DD')
}

/**
 * Object containing utility functions related to date and time handling.
 *
 * @property {function} formatDate - Function to format a date.
 * @property {function} isValidDate - Function to check the validity of a date.
 * @property {function} getTzTodayDay - Function to get today's date in a specific timezone.
 * @property {function} formatTzDateTime - Function to format a date and time for a specific timezone.
 */
const payload = { formatDate, isValidDate, getTzTodayDay, formatTzDateTime }

export default payload
